import { createStore } from "zustand/vanilla";

import type { CityLocation, LayerKey } from "../domain/types";

const layerKeys = new Set<LayerKey>([
  "observed", "forecast", "wind7", "wind10", "wind12", "rain", "cities",
]);

export interface UrlAppState {
  stormKey: string | null;
  selectedCityId: string | null;
  pinnedCityIds: string[];
  currentHour: number;
  hiddenLayers: LayerKey[];
}

export function parseUrlState(search: string): UrlAppState {
  const params = new URLSearchParams(search);
  const hour = Number(params.get("hour") ?? 0);
  const uniquePins = [...new Set((params.get("pins") ?? "").split(",").filter(Boolean))].slice(0, 5);
  const hiddenLayers = [...new Set((params.get("layers") ?? "").split(","))]
    .filter((layer): layer is LayerKey => layerKeys.has(layer as LayerKey));
  return {
    stormKey: params.get("storm") || null,
    selectedCityId: params.get("city") || null,
    pinnedCityIds: uniquePins,
    currentHour: Number.isFinite(hour) ? Math.max(0, Math.min(72, hour)) : 0,
    hiddenLayers,
  };
}

export function serializeUrlState(state: UrlAppState): string {
  const params = new URLSearchParams();
  if (state.stormKey) params.set("storm", state.stormKey);
  if (state.selectedCityId) params.set("city", state.selectedCityId);
  if (state.pinnedCityIds.length) params.set("pins", state.pinnedCityIds.slice(0, 5).join(","));
  if (state.currentHour) params.set("hour", String(Math.max(0, Math.min(72, state.currentHour))));
  if (state.hiddenLayers.length) params.set("layers", state.hiddenLayers.join(","));
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export interface TyphoonAppState {
  stormKey: string;
  selectedCity: CityLocation | null;
  pinnedCities: CityLocation[];
  currentHour: number;
  isPlaying: boolean;
  hiddenLayers: LayerKey[];
  selectedWeatherRowId: string | null;
  lastStormUpdate: string | null;
  selectCity: (city: CityLocation) => void;
  pinCity: (city: CityLocation) => void;
  unpinCity: (cityId: string) => void;
  setCurrentHour: (hour: number) => void;
  setPlaying: (playing: boolean) => void;
  setStormKey: (stormKey: string) => void;
  toggleLayer: (layer: LayerKey) => void;
  selectWeatherRow: (rowId: string | null) => void;
  acknowledgeStormRefresh: (updatedAt: string) => void;
  hydrateUrlState: (state: Partial<UrlAppState>) => void;
}

const defaultCities: CityLocation[] = [
  { id: "101020100", name: "上海", adm1: "上海市", adm2: "上海市", latitude: 31.2304, longitude: 121.4737 },
  { id: "101210401", name: "宁波", adm1: "浙江省", adm2: "宁波市", latitude: 29.8683, longitude: 121.544 },
  { id: "101230101", name: "福州", adm1: "福建省", adm2: "福州市", latitude: 26.0745, longitude: 119.2965 },
];

export function createTyphoonAppStore() {
  return createStore<TyphoonAppState>((set) => ({
    stormKey: "qweather:NW_PACIFIC:2026:NP_2609",
    selectedCity: defaultCities[0] ?? null,
    pinnedCities: defaultCities,
    currentHour: 0,
    isPlaying: false,
    hiddenLayers: [],
    selectedWeatherRowId: null,
    lastStormUpdate: null,
    selectCity: (city) => set({ selectedCity: city }),
    pinCity: (city) => set((state) => {
      const withoutCity = state.pinnedCities.filter((pinned) => pinned.id !== city.id);
      return { pinnedCities: [...withoutCity, city].slice(-5) };
    }),
    unpinCity: (cityId) => set((state) => ({ pinnedCities: state.pinnedCities.filter((city) => city.id !== cityId) })),
    setCurrentHour: (hour) => set({ currentHour: Math.max(0, Math.min(72, hour)) }),
    setPlaying: (isPlaying) => set({ isPlaying }),
    setStormKey: (stormKey) => set({ stormKey }),
    toggleLayer: (layer) => set((state) => ({
      hiddenLayers: state.hiddenLayers.includes(layer)
        ? state.hiddenLayers.filter((current) => current !== layer)
        : [...state.hiddenLayers, layer],
    })),
    selectWeatherRow: (selectedWeatherRowId) => set({ selectedWeatherRowId }),
    acknowledgeStormRefresh: (lastStormUpdate) => set({ lastStormUpdate }),
    hydrateUrlState: (urlState) => set((state) => {
      const cityCatalog = new Map(
        [...defaultCities, ...state.pinnedCities].map((city) => [city.id, city]),
      );
      const hydratedPins = urlState.pinnedCityIds === undefined
        ? state.pinnedCities
        : urlState.pinnedCityIds
            .map((id) => cityCatalog.get(id))
            .filter((city): city is CityLocation => city !== undefined)
            .slice(0, 5);
      const selectedCity = typeof urlState.selectedCityId === "string"
        ? cityCatalog.get(urlState.selectedCityId) ?? state.selectedCity
        : state.selectedCity;
      return {
        stormKey: urlState.stormKey ?? state.stormKey,
        selectedCity,
        pinnedCities: hydratedPins,
        currentHour: urlState.currentHour === undefined
          ? state.currentHour
          : Math.max(0, Math.min(72, urlState.currentHour)),
        hiddenLayers: urlState.hiddenLayers ?? state.hiddenLayers,
      };
    }),
  }));
}

export const typhoonAppStore = createTyphoonAppStore();
